import { createContext, useContext, useEffect, useState, ReactNode } from 'react';

interface GosadUser {
  id: string;
  email: string;
  name?: string;
  [key: string]: unknown;
}

interface GosadAuthContextType {
  user: GosadUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

declare global {
  interface Window {
    GosadAuth: {
      requireAuth: () => Promise<GosadUser>;
    };
  }
}

const GosadAuthContext = createContext<GosadAuthContextType | undefined>(undefined);

// DEV BYPASS: Set to true to skip GosadAuth in localhost
const DEV_AUTH_BYPASS = import.meta.env.DEV && window.location.hostname === 'localhost';

export function GosadAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<GosadUser | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    // DEV BYPASS: Skip auth on localhost
    if (DEV_AUTH_BYPASS) {
      console.log('🔓 DEV MODE: Auth bypass enabled');
      setUser({ id: 'dev-user', email: 'dev@localhost', name: 'Dev User' });
      setIsLoading(false);
      return;
    }

    const waitForGosadAuth = (maxAttempts = 50, interval = 100): Promise<void> => {
      return new Promise((resolve, reject) => {
        let attempts = 0;
        const check = () => {
          if (window.GosadAuth) {
            resolve();
          } else if (attempts >= maxAttempts) {
            reject(new Error('GosadAuth not loaded after timeout'));
          } else {
            attempts++;
            setTimeout(check, interval);
          }
        };
        check();
      });
    };

    const initAuth = async () => {
      try {
        // Attendre que le script GosadAuth soit chargé (max 5 secondes)
        await waitForGosadAuth();

        // Vérifier l'authentification via GosadAuth
        const authenticatedUser = await window.GosadAuth.requireAuth();
        setUser(authenticatedUser);
      } catch {
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  const value: GosadAuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
  };

  // Afficher un loader pendant la vérification de l'authentification
  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          fontSize: '18px',
          color: '#666',
        }}
      >
        Vérification de l'authentification...
      </div>
    );
  }

  return <GosadAuthContext.Provider value={value}>{children}</GosadAuthContext.Provider>;
}

export function useGosadAuth() {
  const context = useContext(GosadAuthContext);
  if (context === undefined) {
    throw new Error('useGosadAuth must be used within a GosadAuthProvider');
  }
  return context;
}
